import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Layers,
  User,
  Users,
  Inbox,
  Handshake,
  BellPlus,
  ShoppingCart,
  Settings2,
  BadgeIndianRupee,
  ListChecks,
  CalendarDays,
  BookOpen,
  TicketPercent,
  ChartScatter,
  FileText,
  Megaphone,
  Package,
  LayoutGrid,
  Ticket,
  Stethoscope,
} from "lucide-react";
import { NavItemWithChildren } from "./nav-item-with-children";
import { Button } from "../ui/button";
import { useState, useCallback, useEffect, memo } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  getSidebarExpandedState,
  setSidebarExpandedState,
  getSidebarOpenMenus,
  setSidebarOpenMenus,
} from "../../utils/sidebar-state";

type SubNavItem = { name: string; href: string };
type NavItem = {
  name: string;
  icon: React.ReactNode;
  href?: string;
  children?: SubNavItem[];
};

const navItems: NavItem[] = [
  { name: "Dashboard", icon: <LayoutGrid size={20} />, href: "/dashboard" },
  { name: "Tickets", icon: <Ticket size={20} />, href: "/dashboard/tickets" },
  { name: "Members", icon: <Users size={20} />, href: "/dashboard/members" },
  {
    name: "Users",
    icon: <User size={20} />,
    href: "/dashboard/users",
  },
  {
    name: "Partners",
    icon: <Handshake size={20} />,
    href: "/dashboard/partners",
  },
  { name: "Sessions", icon: <Layers size={20} />, href: "/dashboard/sessions" },
  {
    name: "Requests",
    icon: <Inbox size={20} />,
    href: "/dashboard/session-assignment-requests",
  },
  {
    name: "Subscriptions",
    icon: <BellPlus size={20} />,
    href: "/dashboard/subscriptions",
  },
  { name: "Orders", icon: <ShoppingCart size={20} />, href: "/dashboard/orders" },
  {
    name: "Products",
    icon: <Package size={20} />,
    href: "/dashboard/products",
  },
  {
    name: "Configurations",
    icon: <Settings2 size={20} />,
    children: [
      {
        name: "Earnings Setup",
        href: "/dashboard/configuration/earnings-setup",
      },
      {
        name: "Performance Rules",
        href: "/dashboard/configuration/performance-rules",
      },
    ],
  },
  {
    name: "Earnings",
    icon: <BadgeIndianRupee size={20} />,
    children: [
      {
        name: "Monthly Earnings",
        href: "/dashboard/earnings/monthly-earnings",
      },
      { name: "Wallet Payouts", href: "/dashboard/earnings/wallet-payouts" },
    ],
  },
  {
    name: "Tasks",
    icon: <ListChecks size={20} />,
    children: [
      { name: "Operations", href: "/dashboard/tasks/operations" },
      { name: "Onboarding", href: "/dashboard/tasks/onboarding" },
    ],
  },
  {
    name: "Leaves",
    icon: <CalendarDays size={20} />,
    href: "/dashboard/leaves",
  },
  {
    name: "Vets",
    icon: <Stethoscope size={20} />,
    children: [
      { name: "Listings", href: "/dashboard/vets" },
      { name: "Submissions", href: "/dashboard/vets/submissions" },
      { name: "Reviews", href: "/dashboard/vets/reviews" },
    ],
  },
  {
    name: "Supply Planning",
    icon: <ChartScatter size={20} />,
    href: "/dashboard/supply-planning",
  },
  { name: "Blogs", icon: <FileText size={20} />, href: "/dashboard/blogs" },
  {
    name: "Banners",
    icon: <Megaphone size={20} />,
    href: "/dashboard/banners",
  },
  {
    name: "Discounts",
    icon: <TicketPercent size={20} />,
    href: "/dashboard/discounts",
  },
  {
    name: "Docs",
    icon: <BookOpen size={20} />,
    children: [
      { name: "Session Requests", href: "/dashboard/docs/session-requests" },
    ],
  },
];

interface IAppSidebarProps {
  onExpandChange?: (expanded: boolean) => void;
}

export const AppSidebar = memo((props: IAppSidebarProps) => {
  const { onExpandChange } = props;

  // Initialize state from localStorage for persistence across reloads
  const [expanded, setExpanded] = useState(getSidebarExpandedState);
  const [openMenus, setOpenMenus] = useState(getSidebarOpenMenus);

  const navigate = useNavigate();
  const location = useLocation();
  const hoverBg = "hover:bg-white/10";

  useEffect(() => {
    if (onExpandChange) {
      onExpandChange(expanded);
    }
  }, [expanded, onExpandChange]);

  const handleToggle = useCallback(() => {
    setExpanded((prev) => {
      const newExpanded = !prev;
      setSidebarExpandedState(newExpanded);
      return newExpanded;
    });
  }, []);

  const toggleMenu = useCallback((menuKey: string) => {
    setOpenMenus((prev) => {
      const newOpenMenus = { ...prev, [menuKey]: !prev[menuKey] };
      setSidebarOpenMenus(newOpenMenus);
      return newOpenMenus;
    });
  }, []);

  const handleLogout = useCallback(() => {
    // Clear authentication data
    // TODO: use zustand, redux, or context api
    localStorage.removeItem("authToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userId");
    // Redirect to login page
    navigate("/");
  }, [navigate]);

  return (
    <TooltipProvider>
      <aside
        className={`h-screen bg-black text-white flex flex-col transition-all duration-300 ${expanded ? "w-60" : "w-20"
          }`}
      >
        {/* Header with logo and toggle */}
        <div className="flex justify-between items-center px-6 py-4">
          <div className="text-orange-500 text-xl font-bold">
            {expanded ? "sploot" : ""}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggle}
            className="text-white hover:bg-white/10"
          >
            {expanded ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </Button>
        </div>

        {/* Navigation items */}
        <nav className="flex-1 flex flex-col overflow-y-auto">
          {navItems.map((item) => {
            const hasChildren =
              Array.isArray(item.children) && item.children.length > 0;
            const isDisabled = item.href === "#" && !hasChildren;

            if (hasChildren) {
              const childItems: SubNavItem[] = item.children
                ? item.children
                : [];
              const isAnyChildActive = childItems.some(
                (c) => location.pathname === c.href,
              );
              const isOpen = openMenus[item.name] ?? isAnyChildActive;

              return (
                <NavItemWithChildren
                  key={item.name}
                  name={item.name}
                  icon={item.icon}
                  children={childItems}
                  expanded={expanded}
                  isOpen={isOpen}
                  isAnyChildActive={isAnyChildActive}
                  hoverBg={hoverBg}
                  onToggle={() => toggleMenu(item.name)}
                />
              );
            }

            const isActive = location.pathname === item.href;

            return (
              <Tooltip key={item.name}>
                <TooltipTrigger asChild>
                  {isDisabled ? (
                    <div className="flex items-center gap-3 w-full px-6 py-3 text-gray-500 cursor-not-allowed">
                      <span>{item.icon}</span>
                      {expanded && <span>{item.name}</span>}
                    </div>
                  ) : (
                    <Link
                      to={item.href ?? "#"}
                      className={`flex items-center gap-3 w-full px-6 py-3 transition-colors ${isActive ? "bg-orange-500 text-white" : hoverBg
                        }`}
                    >
                      <span>{item.icon}</span>
                      {expanded && <span>{item.name}</span>}
                    </Link>
                  )}
                </TooltipTrigger>
                {!expanded && (
                  <TooltipContent side="right">{item.name}</TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </nav>

        {/* Bottom control area */}
        <div className="mt-auto border-t border-white/10">
          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                className="flex items-center gap-3 w-full px-6 py-3 text-white hover:bg-white/10 transition-colors"
                to="/profile"
              >
                <Settings size={20} />
                {expanded && <span>Settings</span>}
              </Link>
            </TooltipTrigger>
            {!expanded && (
              <TooltipContent side="right">Settings</TooltipContent>
            )}
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Link
                onClick={handleLogout}
                to="/"
                className="flex items-center gap-3 w-full px-6 py-3 text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <LogOut size={20} />
                {expanded && <span>Logout</span>}
              </Link>
            </TooltipTrigger>
            {!expanded && <TooltipContent side="right">Logout</TooltipContent>}
          </Tooltip>
        </div>
      </aside>
    </TooltipProvider>
  );
});

AppSidebar.displayName = "AppSidebar";
